import {Badge, Typography} from '@sollapay/ui/components';
import {parseMoney} from '@sollapay/utils';
import {useCallback, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useLocation, useNavigate} from 'react-router-dom';

import {ErrorAlertMessage} from '@/components';
import {
  useTrustAccountPermissions,
  useTrustTypeStrategy
} from '@/features/trust-accounts/providers';
import {TrustAccountPageLayout} from '@/features/trust-accounts/view/components';
import {PageContent} from '@/layouts';

import {useBuyerDeleteFlow, usePurchaseBuyers} from '../../hooks';
import {DeleteBuyerModal} from '../../modals';
import {LEGACY_ASSET_PARAM, PURCHASE_PARAM, useBuyerPurchaseParam} from '../hooks';
import {BuyerAssetContent} from './BuyerAssetContent';
import {BuyerAssetTabs} from './BuyerAssetTabs';
import {BuyerViewHeader} from './BuyerViewHeader';
import {EditBuyerModal} from '../../modals/EditBuyerModal';
import {InitiatePaymentFlow} from '../../modals/InitiatePaymentFlow';
import {
  canRequestMorePayment,
  formatPurchaseAssetNames,
  formatBuyerDisplayName,
  toBuyerList
} from '../../utils';

import type {BuyerDTO, BuyerWithDocumentsDTO, PaymentInstructionDTO} from '@sollapay/types';
import type {FC} from 'react';

interface BuyerViewProps {
  buyer: BuyerWithDocumentsDTO;
  trustAccountId: string;
  trustAccountName: string;
  payments: PaymentInstructionDTO[];
  paymentsError?: boolean;
}

export const BuyerView: FC<BuyerViewProps> = ({
  buyer,
  trustAccountId,
  trustAccountName,
  payments,
  paymentsError
}) => {
  const {t} = useTranslation('trustAccounts');
  const navigate = useNavigate();
  const {search} = useLocation();
  const strategy = useTrustTypeStrategy();
  const {canUpdateBuyer, canDeleteBuyer, canCollectPayments} = useTrustAccountPermissions();
  const [isPaymentOpen, setIsPaymentOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const deleteFlow = useBuyerDeleteFlow(trustAccountId, () =>
    // The buyer no longer has this deal — nothing left to show at this URL.
    navigate(`/trust-accounts/${trustAccountId}/buyers`)
  );

  const assetLabels = useMemo(
    () => buyer.purchases.map(p => formatPurchaseAssetNames(p.assets, t)),
    [buyer.purchases, t]
  );

  const {selectedIndex, setSelectedPurchase} = useBuyerPurchaseParam(buyer.purchases);

  const selectedPurchase = buyer.purchases[selectedIndex];
  const {buyers: purchaseBuyers} = usePurchaseBuyers(
    trustAccountId,
    selectedPurchase?.id,
    buyer.id
  );
  const buyersForPayment = purchaseBuyers.length > 0 ? purchaseBuyers : [buyer];
  const assetCount = buyer.purchases.length;

  const canRequestPayment = useMemo(() => {
    if (!selectedPurchase) return false;
    const maxCollectableNis = parseMoney(selectedPurchase.purchasePriceNis)
      .percentOf(strategy.maxCollectionPercentage)
      .toString();
    return canRequestMorePayment(
      selectedPurchase,
      payments,
      maxCollectableNis,
      strategy.enforcesCollectionCap
    );
  }, [selectedPurchase, payments, strategy]);

  const handleRequestPayment = useCallback(() => setIsPaymentOpen(true), []);
  const handleClosePayment = useCallback(() => setIsPaymentOpen(false), []);
  const handleEditBuyer = useCallback(() => setIsEditOpen(true), []);
  const handleCloseEdit = useCallback(() => setIsEditOpen(false), []);
  const handleBuyerUpdated = useCallback(
    (updatedBuyer: BuyerDTO, previousBuyerId: string) => {
      if (updatedBuyer.id === previousBuyerId) return;
      // Pin the deal on screen — the moved purchase may not be the new buyer's first.
      const params = new URLSearchParams(search);
      params.delete(LEGACY_ASSET_PARAM);
      if (selectedPurchase) params.set(PURCHASE_PARAM, selectedPurchase.id);
      navigate(
        {
          pathname: `/trust-accounts/${trustAccountId}/buyers/${updatedBuyer.id}`,
          search: params.toString()
        },
        {replace: true}
      );
    },
    [navigate, search, selectedPurchase, trustAccountId]
  );
  const handleDeleteBuyer = useCallback(
    () => selectedPurchase && deleteFlow.open(buyer, selectedPurchase),
    [deleteFlow, buyer, selectedPurchase]
  );

  return (
    <TrustAccountPageLayout>
      <BuyerViewHeader
        buyer={buyer}
        trustAccountId={trustAccountId}
        trustAccountName={trustAccountName}
        onEditBuyer={canUpdateBuyer ? handleEditBuyer : undefined}
        onDeleteBuyer={
          canDeleteBuyer && buyer.canDelete && selectedPurchase ? handleDeleteBuyer : undefined
        }
      />

      <PageContent className="flex flex-col gap-8">
        {paymentsError && <ErrorAlertMessage error={t('view.buyers.detail.paymentsError')} />}
        {/* Purchased units heading + asset tabs on same row */}
        <div className="flex items-center justify-between border-b border-border-subtle pb-4">
          <div className="flex items-center gap-2">
            <Typography size="lg" weight="semibold">
              {t('view.buyers.detail.purchasedUnits')}
            </Typography>
            <Badge variant="neutral" size="lg">
              {t('view.buyers.detail.unitCount', {count: assetCount})}
            </Badge>
          </div>
          {assetCount > 1 && (
            <BuyerAssetTabs
              assets={assetLabels}
              selectedIndex={selectedIndex}
              onSelect={setSelectedPurchase}
            />
          )}
        </div>

        {/* Per-asset content */}
        {selectedPurchase && (
          <BuyerAssetContent
            purchase={selectedPurchase}
            identityDocuments={buyer.identityDocuments}
            instructions={payments}
            trustAccountId={trustAccountId}
            buyerId={buyer.id}
            onRequestPayment={canCollectPayments ? handleRequestPayment : undefined}
            requestPaymentDisabled={!canRequestPayment}
            purchaseBuyers={purchaseBuyers}
          />
        )}
      </PageContent>

      <InitiatePaymentFlow
        trustAccountId={trustAccountId}
        buyers={buyersForPayment}
        isOpen={isPaymentOpen}
        onClose={handleClosePayment}
        payments={payments}
        selectedPurchase={selectedPurchase}
      />

      <EditBuyerModal
        trustAccountId={trustAccountId}
        buyers={toBuyerList(isEditOpen ? buyer : null)}
        onClose={handleCloseEdit}
        onBuyerUpdated={handleBuyerUpdated}
      />

      <DeleteBuyerModal
        buyerName={
          deleteFlow.target ? formatBuyerDisplayName(deleteFlow.target.buyer.fullName) : null
        }
        onConfirm={deleteFlow.confirm}
        onClose={deleteFlow.close}
        isLoading={deleteFlow.isLoading}
        error={deleteFlow.error}
      />
    </TrustAccountPageLayout>
  );
};
